import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatCount } from '../../lib/formatters';
import { usePunctualityDistribution } from '../../services/api/hooks/usePunctualityDistribution';
import type { PunctualityBucket } from '../../types/extranet-api.types';
import { useFilterStore } from '../filters/filterStore';

const RANGE_LABEL: Record<PunctualityBucket['range'], string> = {
  under_5: '< 5 min',
  '5_10': '5–10 min',
  '10_15': '10–15 min',
  '15_30': '15–30 min',
  over_30: '> 30 min',
};

const RANGE_COLOR: Record<PunctualityBucket['range'], string> = {
  under_5: '#18181b',
  '5_10': '#3f3f46',
  '10_15': '#a1a1aa',
  '15_30': '#d4d4d8',
  over_30: '#e4e4e7',
};

const ORDER: PunctualityBucket['range'][] = ['under_5', '5_10', '10_15', '15_30', 'over_30'];

export const PunctualityHistogram = () => {
  const { entityIds, contractIds, siteIds, dateRange } = useFilterStore();
  const query = usePunctualityDistribution({ entityIds, contractIds, siteIds, dateRange });

  const buckets = query.data?.buckets ?? [];
  const total = buckets.reduce((sum, b) => sum + b.count, 0);

  const data = ORDER.map(range => {
    const bucket = buckets.find(b => b.range === range);
    const count = bucket?.count ?? 0;
    return {
      range,
      label: RANGE_LABEL[range],
      count,
      pct: total > 0 ? (count / total) * 100 : 0,
    };
  });

  const onTimeCount = data
    .filter(d => d.range === 'under_5' || d.range === '5_10')
    .reduce((sum, d) => sum + d.count, 0);
  const onTimePct = total > 0 ? (onTimeCount / total) * 100 : 0;

  return (
    <article className="rounded-md border border-zinc-200 bg-white p-6 flex flex-col gap-5">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-sm font-medium text-zinc-900">Distribution des retards</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Seuil contractuel de ponctualité : 10 min</p>
        </div>
        {!query.isLoading && total > 0 ? (
          <div className="flex items-center gap-4 text-xs">
            <span className="text-zinc-500">
              <span className="font-mono tabular-nums text-zinc-900">{onTimePct.toFixed(1)} %</span> à l’heure
            </span>
            <span className="text-zinc-300">·</span>
            <span className="font-mono tabular-nums text-zinc-500">{formatCount(total)} courses</span>
          </div>
        ) : null}
      </header>

      {query.isLoading ? (
        <div className="h-[280px] rounded-sm bg-zinc-50 skeleton-pulse" />
      ) : total === 0 ? (
        <EmptyState />
      ) : (
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 16, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#f4f4f5" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="#a1a1aa"
                tick={{ fontSize: 11, fontFamily: 'Geist, system-ui, sans-serif' }}
                tickLine={false}
                axisLine={{ stroke: '#e4e4e7' }}
              />
              <YAxis
                stroke="#a1a1aa"
                tick={{ fontSize: 11, fontFamily: 'Geist Mono, ui-monospace, monospace' }}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={v => formatCount(v as number)}
              />
              <Tooltip
                cursor={{ fill: 'rgba(244, 244, 245, 0.5)' }}
                contentStyle={{
                  background: '#ffffff',
                  border: '1px solid #e4e4e7',
                  borderRadius: 4,
                  fontSize: 12,
                  padding: '8px 12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
                labelStyle={{ color: '#71717a', fontSize: 11 }}
                formatter={(_value, _name, item) => {
                  const datum = item.payload as { count: number; pct: number };
                  return [`${formatCount(datum.count)} courses · ${datum.pct.toFixed(1)} %`, 'Volume'];
                }}
              />
              <ReferenceLine
                x="10–15 min"
                stroke="#dc2626"
                strokeDasharray="3 3"
                strokeWidth={1}
                label={{
                  value: 'Seuil 10 min',
                  position: 'top',
                  fill: '#dc2626',
                  fontSize: 10,
                  fontFamily: 'Geist, system-ui, sans-serif',
                }}
              />
              <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={64}>
                {data.map(d => (
                  <Cell key={d.range} fill={RANGE_COLOR[d.range]} />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  fontSize={11}
                  fontFamily="Geist Mono, ui-monospace, monospace"
                  fill="#52525b"
                  formatter={(v: unknown) => formatCount(v as number)}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
};

const EmptyState = () => (
  <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-center">
    <p className="text-sm text-zinc-900">Aucune donnée de ponctualité sur cette période.</p>
    <p className="text-xs text-zinc-500">Ajustez les filtres pour afficher davantage de résultats.</p>
  </div>
);
